import { FC, PropsWithChildren, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useLocation, useNavigate, useParams } from 'react-router'

import { Flex } from 'antd'

import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { HTML } from '../../components/markup/HTML'
import { Page } from '../../components/page'
import { useResultReviewQuery, useUpcomingRaceLazyQuery } from '../../graphql'
import routes, { generateSlug, generateTitleFromSlug } from '../../routes'
import { client } from '../../services/motogp'

import { Context } from './Context'
import { ContextProps } from './index'
import { ResultsForm } from './ResultsForm'
import { ResultTabs } from './ResultTabs'

const StateHolder: FC<PropsWithChildren<{ eventId: string; categoryId: string; seasonId: string }>> = ({
  eventId,
  categoryId,
  seasonId,
  children,
}) => {
  const params = useParams()
  const [state, setState] = useState<ContextProps['state']>({
    eventId,
    categoryId,
    seasonId,
    sessionId: '',
    params,
  })

  return <Context.Provider value={{ state, setState }}>{children}</Context.Provider>
}

const ParamsContext: FC<PropsWithChildren<ContextProps['state']['params']> & { seasonId: string }> = ({
  children,
  seasonId,
  ...params
}) => {
  const categoriesQuery = useQuery({
    queryKey: ['categories', seasonId],
    queryFn: () => client.categories(seasonId),
    enabled: !!seasonId,
  })

  const eventsQuery = useQuery({
    queryKey: ['events', seasonId],
    queryFn: () => client.events(seasonId),
    enabled: !!seasonId,
  })

  const eventId = eventsQuery?.data?.find((it) => generateSlug(it.circuit.name) === params.track)?.id ?? ''
  const categoryId =
    categoriesQuery?.data?.find((it) => it.name.toLowerCase().startsWith(params.category ?? ''))?.id ?? ''

  if (!eventId || !categoryId) return null

  return (
    <StateHolder seasonId={seasonId} categoryId={categoryId} eventId={eventId}>
      {children}
    </StateHolder>
  )
}

const translations: Record<string, string> = {
  ['250cc']: '250cc',
  ['125cc']: '125cc',
  ['50cc']: '50cc',
  ['500cc']: '500cc',
  ['race']: 'гонки',
  ['practice']: 'практики',
  ['sprint']: 'спринту',
  ['warm-up']: 'warmup',
  ['qualification']: 'кваліфікації',
  ['free-practice']: 'практик',
  ['motogp']: 'МотоGP',
  ['moto2']: 'Мото2',
  ['moto3']: 'Мото3',
}

const DAYS_TO_SHOW_LAST_RACE = 5 as const
const currentDate = dayjs().subtract(DAYS_TO_SHOW_LAST_RACE, 'days').toDate()

const Results: FC = () => {
  const params = useParams<ContextProps['state']['params']>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const seasonsQuery = useQuery({ queryKey: ['seasons'], queryFn: () => client.seasons() })
  const seasonId = seasonsQuery.data?.find((it) => it.year === Number(params.year))?.id ?? ''
  const { formatMessage } = useIntl()
  const [upcomingRace] = useUpcomingRaceLazyQuery({
    variables: { date: currentDate },
  })

  useEffect(() => {
    if (!params.year || !params.category) {
      upcomingRace()
        .then((response) => response.data?.races?.[0]?.track?.slug)
        .then((track) => {
          if (track)
            navigate(
              generatePath(routes.result, { category: 'motogp', track, year: currentDate.getFullYear().toString() }),
            )
        })
    }
  }, [navigate, params.category, params.year, upcomingRace])

  const { data } = useResultReviewQuery({ variables: { url: pathname.replace(routes.results, '') } })

  const review = data?.resultReviews?.[0]

  const title =
    review?.title ??
    [
      formatMessage({ id: 'results.header' }),
      params.session ? translations[params.session] : '',
      params.category ? translations[params.category] : '',
      params.year ? params.year : '',
      generateTitleFromSlug(params.track ?? ''),
    ].join(' ')

  return (
    <Page title={title} seo={{ metaTitle: [title, 'TochkaGP'].join(' | ') }}>
      <Flex vertical gap={32}>
        <ParamsContext seasonId={seasonId} {...params}>
          <ResultsForm />
          <ResultTabs />
          {review?.text ? <HTML>{review.text}</HTML> : null}
        </ParamsContext>
      </Flex>
    </Page>
  )
}

export default Results
